import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import Papa from "papaparse";
import FileSaver from "file-saver";
import { FileText } from "lucide-react";
import InnerLayout from "../../components/layouts/inner-layout";
import TabsComponent from "../../components/helper-components/tabs-component";
import AddButton, { DraftButton } from "../../components/buttons/button";
import successToast from "../../components/helper-components/success-toast";
import errorToast from "../../components/helper-components/error-toast";
import { DataContext } from "../../components/layouts/main-layout";
import {
  downloadAllTemplates,
  downloadAllTemplatesJson,
} from "../../components/helper-components/helper-function";
import FileDropZone from "../../components/helper-components/file-drop-zone";
import SheetPreviewModal from "../../components/helper-components/sheet-preview-modal";
import LoadingSpinner from "../../components/helper-components/loading-spinner";
import DynamicTransactionTable from "../../components/tables/dynamic-transaction-table";
import BadgesComponent from "../../components/tables/badges-component";
import {
  parsePaymentFile,
  parseJsonPaymentFile,
  PaymentFileError,
} from "../../components/store-information/coco-bulk-payment/parse-payment-file";
import {
  ACCEPTED_FILE_EXTENSIONS,
  BULK_PAYMENT_TABS,
  getBulkPaymentContract,
  findMissingHeaders,
  validateBulkPaymentRow,
  toUploadPayload,
} from "../../components/store-information/coco-bulk-payment/bulk-payment-columns";
import {
  cocoBulkPaymentUploadAction,
  cocoPaymentHistoryAction,
} from "../../redux/actions/coco-bulk-payment-action";

const MODULE_NAME = "store_document_process";

const ITEM_TABS = BULK_PAYMENT_TABS;

/**
 * Builds a heading for DynamicTransactionTable.
 *
 * `display: false` keeps the sort control off every column — sorting there calls
 * setFilterData, and this screen runs no server-side filter state. `column:
 * false` for the same reason: it only feeds the filter bar's column picker,
 * which is switched off here (filterSec={false}).
 */
const heading = (label, value, minWidth) => ({
  label,
  value,
  minWidth,
  display: false,
  column: false,
});

// Per-row outcome of an upload: ROW / identifying fields / STATUS / REASON.
// DynamicTransactionTable prints rowData[value] verbatim, so anything needing
// formatting (amount, the status badge) is prepared in resultTableRows below.
const RESULT_HEADING = [
  heading("ROW", "row_number", 60),
  heading("STORE CODE", "store_code", 120),
  heading("LANDLORD A/C NO", "landlord_account_no", 180),
  heading("AMOUNT", "amount", 110),
  heading("TRANSACTION ID", "transaction_id", 170),
  heading("STATUS", "status", 100),
  heading("REASON", "reason", 260),
];

const formatResultAmount = (value) => {
  const numeric = Number(String(value ?? "").replace(/,/g, ""));
  return Number.isFinite(numeric)
    ? numeric.toLocaleString("en-IN")
    : value || "--";
};

// Identifies a row without depending on key order or number/string typing, so a
// returned row can be matched back to the one that was submitted.
const rowSignature = (row) =>
  [row?.store_code, row?.landlord_account_no, row?.amount, row?.transaction_id]
    .map((value) => String(value ?? ""))
    .join("|");

const formatRupees = (value) => {
  const numeric = Number(value);
  return Number.isFinite(numeric)
    ? `\u20b9 ${numeric.toLocaleString("en-IN")}`
    : "--";
};

// Identical for Rent and SD — only the endpoint supplying the rows differs.
// payment_method is not returned by either history endpoint yet, so that column
// reads "--" until the API includes it.
const HISTORY_HEADING = [
  heading("STORE CODE", "store_code", 110),
  heading("STORE NAME", "store_name", 160),
  heading("LANDLORD NAME", "landlord_name", 150),
  heading("LANDLORD A/C NO", "landlord_account_no", 160),
  heading("AMOUNT", "amount", 120),
  heading("IFSC CODE", "ifsc_code", 130),
  heading("PAYMENT METHOD", "payment_method", 150),
  heading("TRANSACTION TYPE", "transaction_type", 160),
  heading("TRANSACTION ID", "transaction_id", 160),
  heading("REMARKS", "remark", 180),
];

/**
 * Maps a bulk-upload response to result-table rows.
 *
 * The endpoint answers with per-row outcomes in two arrays:
 *
 *   data: { total, successCount, failedCount,
 *           successful: [ { row: {…} } ],
 *           failed:     [ { row: {…}, <reason> } ] }
 *
 * Note each entry *wraps* the submitted row under `row` rather than being the
 * row itself. The reason field name isn't fixed across responses, so the usual
 * spellings are accepted; anything unmapped falls back to the raw entry being
 * shown instead (see uploadResponse).
 *
 * `submitted` is the posted payload, used to recover the file's row numbers.
 */
const extractServerRows = (res, submitted = []) => {
  const payload = res?.data ?? {};
  const failed = Array.isArray(payload.failed) ? payload.failed : [];
  const successful = Array.isArray(payload.successful) ? payload.successful : [];

  /**
   * Recovers the line number from the user's sheet.
   *
   * `failed` and `successful` are separate lists, so a position within either
   * one is not the file's row number — with rows 1 and 3 failing out of 3, the
   * failed list would report them as 1 and 2. The submitted row comes back in
   * each entry, so the submitted list is walked instead and the first unclaimed
   * match wins, which stays correct even when rows are identical.
   */
  const claimed = new Set();
  const lineOf = (row, fallback) => {
    const signature = rowSignature(row);
    for (let index = 0; index < submitted.length; index += 1) {
      if (claimed.has(index)) continue;
      if (rowSignature(submitted[index]) === signature) {
        claimed.add(index);
        return index + 1;
      }
    }
    return fallback;
  };

  const toRow = (entry, index, status) => {
    const row = (entry && entry.row) || entry || {};
    const reason =
      entry?.reason ??
      entry?.error ??
      entry?.message ??
      (Array.isArray(entry?.errors) ? entry.errors.join(", ") : "");

    return {
      row_number:
        entry?.rowNumber ??
        entry?.row_number ??
        entry?.index ??
        lineOf(row, index + 1),
      store_code: row.store_code ?? "",
      landlord_account_no: row.landlord_account_no ?? "",
      amount: row.amount ?? "",
      transaction_id: row.transaction_id ?? "",
      status,
      reason: status === "failed" ? reason || "Rejected by the server" : "",
    };
  };

  // Failures first so the problems are at the top, then re-sorted by line so the
  // ROW column still reads in the user's file order.
  return [
    ...failed.map((entry, index) => toRow(entry, index, "failed")),
    ...successful.map((entry, index) => toRow(entry, index, "success")),
  ].sort((a, b) => a.row_number - b.row_number);
};

const CocoBulkPayment = () => {
  const { permissionFn } = useContext(DataContext);
  const dispatch = useDispatch();

  const [currentTab, setCurrentTab] = useState(ITEM_TABS[0]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Pre-flight validation outcome. Only populated when local checks fail, in
  // which case nothing is sent — the rows are shown so they can be corrected.
  const [validation, setValidation] = useState(null);

  // Raw server reply for a rejected upload. Shown verbatim because the response
  // shape is undocumented and its message is the only account of *why* rows were
  // refused; without it a failure is just a count.
  const [uploadResponse, setUploadResponse] = useState(null);

  // Server-side payment history for the active tab.
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [dataCount, setDataCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const canUpload = permissionFn(MODULE_NAME)?.create;

  // Rent and SD differ in column rules and endpoint slug, so every payment-type
  // aware step below (card, template, validation, upload, history) reads here.
  const contract = getBulkPaymentContract(currentTab);

  const resetUpload = () => {
    setSelectedFile(null);
    setValidation(null);
    setUploadResponse(null);
    setPreviewOpen(false);
  };

  // Page and limit are arguments rather than closed-over state so a caller can
  // request a specific page. Refetching after an upload has to ask for page 1
  // explicitly — relying on the closure would request whichever page happened to
  // be showing and race the refetch that setCurrentPage triggers.
  const fetchHistory = useCallback(
    (page, limit) => {
      setHistoryLoading(true);
      dispatch(
        cocoPaymentHistoryAction(
          { slug: contract.slug, page, limit },
          (res) => {
            // Response nests the rows one level deeper: data.data is the array,
            // data.total the unpaginated count.
            const payload = res?.data || {};
            setHistory(Array.isArray(payload.data) ? payload.data : []);
            setDataCount(Number(payload.total) || 0);
            setHistoryLoading(false);
          },
          (err) => {
            setHistory([]);
            setDataCount(0);
            setHistoryLoading(false);
            errorToast(err?.data?.message ?? "Failed to load payment history");
          }
        )
      );
    },
    [dispatch, contract.slug]
  );

  useEffect(() => {
    fetchHistory(currentPage, pageSize);
  }, [fetchHistory, currentPage, pageSize]);

  // Rent and SD are separate files going to separate endpoints, so switching
  // tabs discards whatever was staged and resets pagination.
  const handleTabChange = (tab) => {
    setCurrentTab(tab);
    resetUpload();
    setCurrentPage(1);
  };

  const handleDownloadTemplate = () => {
    downloadAllTemplates([
      {
        documentType: contract.templateName,
        expectedHeaders: contract.columns.map((column) => column.key),
      },
    ]);
  };

  const handleDownloadTemplateJson = () => {
    downloadAllTemplatesJson([
      {
        documentType: contract.templateName,
        expectedHeaders: contract.columns.map((column) => column.key),
      },
    ]);
  };

  // File type is already vetted by FileDropZone against ACCEPTED_FILE_EXTENSIONS,
  // which reports rejections through onError.
  const handleFileSelected = async (file) => {
    if (!file) return;
    setValidation(null);
    setParsing(true);
    try {
      const isJsonFile = /\.json$/i.test(file.name || "");
      const { headers, rows } = isJsonFile
        ? await parseJsonPaymentFile(file)
        : await parsePaymentFile(file);

      if (rows.length === 0) {
        errorToast("The file is empty or contains no valid rows.");
        return;
      }

      const missing = findMissingHeaders(headers, contract.columns);
      if (missing.length > 0) {
        errorToast(`Missing required column(s): ${missing.join(", ")}`);
        return;
      }

      setSelectedFile({
        file,
        name: file.name,
        rowCount: rows.length,
        rows,
        headers,
      });
    } catch (error) {
      // PaymentFileError messages are written for the user, so they surface as
      // they are. For anything else the parser's own message is included — it
      // names the actual defect ("Cell style not found: 3", "xl/sharedStrings.xml
      // file not found inside the .xlsx file"), which is the difference between
      // a reportable bug and a guess.
      console.error("Failed to parse payment file:", error);
      errorToast(
        error instanceof PaymentFileError
          ? error.message
          : `Could not read "${file.name}" — ${
              error?.message || "unknown parser error"
            }`
      );
    } finally {
      setParsing(false);
    }
  };

  const handleUpload = () => {
    // Guarded here rather than only on the button so neither entry point (the
    // staged-file bar or the preview's Done) can upload without permission.
    if (!selectedFile || !canUpload || uploading) return;

    if (selectedFile.rows.length === 0) {
      errorToast(
        "No rows could be read from this file, so there is nothing to upload."
      );
      return;
    }

    const rowCount = selectedFile.rows.length;

    // Pre-flight covers dates and the landlord account number only. Both are easy
    // to get wrong in Excel and produce server errors that read as something else
    // ("landlord not found" for a stray space), so they are worth catching here.
    // Everything else is left to the server.
    const checked = selectedFile.rows.map((row, index) => {
      const reason = validateBulkPaymentRow(row, contract.columns);
      return {
        row_number: index + 1,
        store_code: row?.store_code,
        landlord_account_no: row?.landlord_account_no,
        amount: row?.amount,
        transaction_id: row?.transaction_id,
        status: reason ? "failed" : "success",
        reason: reason || "",
      };
    });

    const invalid = checked.filter((entry) => entry.status === "failed");
    if (invalid.length > 0) {
      setValidation({ rows: checked });
      setUploadResponse(null);
      errorToast(
        `${invalid.length} of ${rowCount} rows have an invalid date or account number. Nothing was uploaded.`
      );
      return;
    }

    setValidation(null);
    setUploadResponse(null);
    setPreviewOpen(false);
    setUploading(true);

    const submitted = toUploadPayload(selectedFile.rows, contract.columns);

    dispatch(
      cocoBulkPaymentUploadAction(
        { slug: contract.slug, rows: submitted },
        (res) => {
          setUploading(false);

          // The endpoint answers HTTP 200 / code 200 even when rows were
          // rejected, so success cannot be inferred from the status code alone.
          const summary = res?.data || {};
          const failedCount = Number(
            summary.failedCount ?? summary.failed?.length ?? 0
          );
          const rejected =
            failedCount > 0 ||
            res?.status === false ||
            /fail/i.test(res?.message || "");

          if (rejected) {
            errorToast(
              res?.message ?? `${failedCount} row(s) were rejected by the server.`
            );
            // Prefer the per-row table; fall back to the raw reply only when the
            // response carries no rows to show, so an unrecognised shape can
            // still be read rather than silently swallowed.
            const serverRows = extractServerRows(res, submitted);
            if (serverRows.length > 0) {
              setValidation({ rows: serverRows });
              setUploadResponse(null);
            } else {
              setValidation(null);
              setUploadResponse(res);
            }
            // The file stays staged so it can be corrected and re-sent.
          } else {
            successToast(
              res?.message ?? `${rowCount} payments uploaded successfully`
            );
            resetUpload();
          }

          // Refresh either way: a partial batch still adds rows.
          // Explicit page 1 so the newest batch shows regardless of which page
          // was open.
          setCurrentPage(1);
          fetchHistory(1, pageSize);
        },
        (err) => {
          setUploading(false);
          errorToast(err?.data?.message ?? "Failed to upload payments");
        }
      )
    );
  };

  const failedRows = useMemo(
    () => (validation?.rows || []).filter((row) => row.status === "failed"),
    [validation]
  );

  // Display copies for the shared table, which renders each cell as
  // rowData[heading.value] with no formatter of its own. validation.rows stays
  // raw so the failed-rows CSV keeps the values as they were submitted.
  const resultTableRows = useMemo(
    () =>
      (validation?.rows || []).map((row) => ({
        ...row,
        store_code: row.store_code || "--",
        landlord_account_no: row.landlord_account_no || "--",
        amount: formatResultAmount(row.amount),
        transaction_id: row.transaction_id || "--",
        // "failed" mirrors the API's `failed` array and is what the row filters
        // and the CSV download key off, but BadgesComponent already calls that
        // state "rejected". Translating here means the shared badge component
        // needs no new status of its own.
        status: (
          <BadgesComponent
            status={row.status === "failed" ? "rejected" : row.status}
          />
        ),
        reason: row.reason || "",
      })),
    [validation]
  );

  const historyTableRows = useMemo(
    () =>
      history.map((row, index) => {
        const display = { id: row?.payment_id ?? index };
        HISTORY_HEADING.forEach(({ value }) => {
          display[value] =
            value === "amount" ? formatRupees(row?.amount) : row?.[value] || "--";
        });
        return display;
      }),
    [history]
  );

  // Two lists rather than one 2-column grid: a grid fills row-wise and would
  // interleave the halves, losing the order the keys appear in the payload.
  const columnGroups = useMemo(() => {
    const half = Math.ceil(contract.columns.length / 2);
    return [contract.columns.slice(0, half), contract.columns.slice(half)];
  }, [contract.columns]);

  const handleDownloadFailedRows = () => {
    const csv = Papa.unparse(
      failedRows.map((row) => ({
        row: row.row_number,
        store_code: row.store_code ?? "",
        landlord_account_no: row.landlord_account_no ?? "",
        amount: row.amount ?? "",
        transaction_id: row.transaction_id ?? "",
        reason: row.reason ?? "",
      }))
    );
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    FileSaver.saveAs(blob, `${contract.templateName}_failed_rows.csv`);
  };

  const handleDownloadFailedRowsJson = () => {
    const json = JSON.stringify(
      failedRows.map((row) => ({
        row: row.row_number,
        store_code: row.store_code ?? "",
        landlord_account_no: row.landlord_account_no ?? "",
        amount: row.amount ?? "",
        transaction_id: row.transaction_id ?? "",
        reason: row.reason ?? "",
      })),
      null,
      2
    );
    const blob = new Blob([json], { type: "application/json" });
    FileSaver.saveAs(blob, `${contract.templateName}_failed_rows.json`);
  };

  // Both .xlsx and .json are parsed before staging, so a staged file always has
  // a real row count.
  const uploadButtonText = `Upload ${selectedFile?.rowCount ?? 0} rows`;

  const paginationProps = {
    pageSize,
    setPageSize,
    dataCount,
    setCurrentPage,
    currentPage,
  };

  return (
    <InnerLayout
      permissionFn={permissionFn}
      module={MODULE_NAME}
      title="COCO Bulk Payment"
    >
      <div className="pt-5 pb-6 h-full flex flex-col">
        <TabsComponent
          itemTabs={ITEM_TABS}
          currentTab={currentTab}
          setCurrentTab={setCurrentTab}
          handleClickfn={handleTabChange}
        />

        <div className="px-[24px] mt-4 flex flex-col gap-4">
          <p className="text-[length:--list-table-row-font-size] text-[var(--dashboard-text-secondary)]">
            Reconcile offline{" "}
            {currentTab === "Rent" ? "rent" : "security-deposit"} transactions by
            uploading a payment file.
          </p>

          {/* Picker + column reference */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <FileDropZone
                accept={ACCEPTED_FILE_EXTENSIONS}
                disabled={!canUpload}
                busy={parsing}
                busyTitle="Reading your file…"
                busyDescription="This can take a moment for large sheets"
                onFilesSelected={(files) => handleFileSelected(files[0])}
                onError={errorToast}
              >
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleDownloadTemplate}
                    className="px-4 py-2 border-radius bg-[var(--trans-stats-con-blue-bg)] text-[var(--primary)] text-[length:--list-table-row-font-size] font-[500]"
                  >
                    Download sample template
                  </button>
                  <button
                    type="button"
                    onClick={handleDownloadTemplateJson}
                    className="px-4 py-2 border-radius bg-[var(--trans-stats-con-blue-bg)] text-[var(--primary)] text-[length:--list-table-row-font-size] font-[500]"
                  >
                    Download sample template (JSON)
                  </button>
                </div>
              </FileDropZone>
            </div>
            {/* Required columns, shown beside the drop zone so the file can be
                checked before it is picked. Rent and SD differ, so the list
                comes from the active tab's contract. */}
            <div
              className="rounded-[12px] border p-4 flex flex-col"
              style={{
                borderColor: "var(--dashboard-gray-200)",
                minHeight: "14rem",
              }}
            >
              <h3 className="text-sm font-semibold text-[var(--dashboard-gray-900)]">
                Required columns
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 mt-3 flex-1">
                {columnGroups.map((group, groupIndex) => (
                  <ul key={groupIndex} className="flex flex-col gap-y-[6px]">
                    {group.map((column) => (
                      <li
                        key={column.key}
                        className="text-[length:--list-table-row-font-size] text-[var(--dashboard-text-secondary)] flex items-start gap-2"
                      >
                        <span aria-hidden="true">•</span>
                        <span className="break-all">{column.label}</span>
                      </li>
                    ))}
                  </ul>
                ))}
              </div>

              <hr
                className="my-3"
                style={{ borderColor: "var(--dashboard-gray-200)" }}
              />

              {contract.matchNotes.map((note) => (
                <p
                  key={note}
                  className="text-[length:--list-table-row-font-size] text-[var(--dashboard-text-tertiary)]"
                >
                  {note}
                </p>
              ))}
            </div>
          </div>

          {/* Staged file */}
          {selectedFile && (
            <div className="rounded-[10px] bg-[var(--dashboard-gray-100)] px-4 py-3 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <FileText size={20} color="var(--dashboard-gray-500)" />
                <div className="flex flex-col min-w-0">
                  <span className="text-[length:--list-table-row-font-size] font-[500] text-[var(--dashboard-text-main)] truncate">
                    {selectedFile.name}
                  </span>
                  <span className="text-[length:--list-table-row-font-size] text-[var(--dashboard-text-tertiary)]">
                    {selectedFile.rowCount} rows detected
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-3 flex-shrink-0">
                {/* Preview reads the rows parsed in the browser, so it is only
                    available for files this app can parse (see .xlsx note in
                    handleFileSelected). */}
                {selectedFile.rows.length > 0 && (
                  <DraftButton
                    text="Preview"
                    onclkFn={() => setPreviewOpen(true)}
                  />
                )}
                {canUpload && (
                  <AddButton
                    prefixIcon={false}
                    text={uploadButtonText}
                    loading={uploading}
                    onclkFn={handleUpload}
                  />
                )}
              </div>
            </div>
          )}

          {/* Server's own account of a rejected upload. Rendered verbatim
              because its message is often the only stated reason. */}
          {uploadResponse && (
            <div className="rounded-[10px] border border-[var(--status-badge-red-fg)] bg-[var(--status-badge-red-bg)] px-4 py-3 flex flex-col gap-2">
              <span className="text-sm font-[600] text-[var(--status-badge-red-fg)]">
                Server response
              </span>
              <pre className="text-[length:--list-table-row-font-size] text-[var(--dashboard-text-main)] whitespace-pre-wrap break-words m-0 max-h-[220px] overflow-auto">
                {JSON.stringify(uploadResponse, null, 2)}
              </pre>
            </div>
          )}
        </div>

        {/* The two tables sit outside the padded column above:
            DynamicTransactionTable brings its own px-[24px], and it sizes
            itself against its parent, so each gets a fixed-height block to
            scroll inside rather than growing down the page. */}

        {/* Rejected rows — from pre-flight validation, or from the server's
            per-row detail when an upload comes back with failures */}
        {validation && (
          <div className="flex flex-col mt-4 flex-shrink-0">
            <div className="h-[420px] flex-shrink-0">
              <DynamicTransactionTable
                tableHeading={RESULT_HEADING}
                tableBodyData={resultTableRows}
                permissionFn={permissionFn}
                module={MODULE_NAME}
                label="Row"
                filterSec={false}
                filter={false}
                list={false}
                menu={false}
                date={false}
                addNew={false}
                is_checkbox_req={false}
                showPagination={false}
              />
            </div>

            {failedRows.length > 0 && (
              <div className="px-[24px] flex items-center gap-3">
                <AddButton
                  prefixIcon={false}
                  text={`Download failed rows (${failedRows.length})`}
                  onclkFn={handleDownloadFailedRows}
                  className="bg-[var(--status-badge-red-bg)] text-[var(--status-badge-red-fg)] px-6 py-2 border-radius flex justify-center items-center fw_500 text-sm whitespace-nowrap"
                />
                <AddButton
                  prefixIcon={false}
                  text="Download failed rows (JSON)"
                  onclkFn={handleDownloadFailedRowsJson}
                  className="bg-[var(--status-badge-red-bg)] text-[var(--status-badge-red-fg)] px-6 py-2 border-radius flex justify-center items-center fw_500 text-sm whitespace-nowrap"
                />
              </div>
            )}
          </div>
        )}

        {/* Upload history */}
        <div className="flex flex-col mt-4 flex-shrink-0">
          <h3 className="px-[24px] text-sm font-semibold text-[var(--dashboard-gray-900)]">
            {currentTab === "Rent" ? "Rent" : "Security deposit"} payment history
          </h3>
          {historyLoading ? (
            <LoadingSpinner />
          ) : (
            <div className="h-[520px] flex-shrink-0">
              <DynamicTransactionTable
                tableHeading={HISTORY_HEADING}
                tableBodyData={historyTableRows}
                paginationProps={paginationProps}
                permissionFn={permissionFn}
                module={MODULE_NAME}
                label="Payment"
                filterSec={false}
                filter={false}
                list={false}
                menu={false}
                date={false}
                addNew={false}
                is_checkbox_req={false}
                showPagination={true}
              />
            </div>
          )}
        </div>
      </div>

      <SheetPreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        onDone={handleUpload}
        fileName={selectedFile?.name || ""}
        headers={selectedFile?.headers || []}
        rows={selectedFile?.rows || []}
        uploading={uploading}
        canUpload={canUpload}
      />
    </InnerLayout>
  );
};

export default CocoBulkPayment;
